'use strict';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { v4 as generateUuid } from 'uuid';

// Components
import MobileGrid from './Editor/grid/MobileGrid.react';
import SecondaryActions from './Editor/actions/SecondaryActions.react';
import PrimaryActions from './Editor/actions/PrimaryActions.react';
import MenuStats from './Editor/shared/MenuStats.react';
import DietitianBanner from './Editor/shared/DietitianBanner.react';
import AddSwapWrapper from './Editor/wrappers/AddSwapWrapper.react';
import EditMealServingsWrapper from './Editor/wrappers/EditMealServingsWrapper.react';
import NutritionModal from './Editor/modals/nutrition/NutritionModal.react';
import PickDateModal from './Editor/modals/utility/PickDateModal.react';
import ConfirmOverwriteModal from './Editor/modals/conflict/ConfirmOverwriteModal.react';
import LeftoverOffsetsModal from './Editor/modals/leftover/LeftoverOffsetsModal.react';
import MealRepeatModal from './Editor/modals/repeat/MealRepeatModal.react';
import GroceriesModal from './Editor/modals/groceries/GroceriesModal.react';
import MealDetails from './Editor/modals/details/MealDetails.react';
import ProfileModal from './Editor/modals/profile/ProfileModal.react';
import RecipeEditorModal from '@components/Recipes/Modals/RecipeEditorModal.react';
import FoodEditorModal from '@components/Foods/Modals/FoodEditorModal/FoodEditorModal.react';
import RecommendModal from '@pro/components/Modals/RecommendModal.react';
import AgreementModal from '@pro/components/Modals/AgreementModal.react';
import Alert from '@components/Widgets/Alert/Alert.react';
import LogPortionsModalWrapper from '@components/Plans/Editor/wrappers/LogPortionsModalWrapper.react';
import WorkingVeil from './Editor/shared/WorkingVeil.react';

// Stores
import UserStore from '@stores/UserStore';

// Custom Hooks
import { useMealPlan } from './Editor/hooks/useMealPlan';
import { useFavorites } from './Editor/hooks/useFavorites';

// Contexts
import {
    PermissionsProvider,
    AssetsProvider,
    MealPlanProvider,
    MealOperationsProvider,
    ModalProvider,
    UIStateProvider
} from './Editor/contexts';

// Utilities
import { getPermissions } from '@utils/PlanPermissions';
import { genericizeProfileParticipants } from '@utils/Meals';
import Analytics from '@utils/Analytics';
import './Editor.scss';
import "@pro/components/Widgets/Markdown.scss";


/**
 * Editor - Fully functional component version of the meal plan editor
 *
 * This version uses modern React patterns throughout, eliminating the class component
 * and the legacy context API.
 */
const Editor = ({
    uuid,
    patient,
    profile: providedProfile,
    location,
    onPlanUuidChanged,
    onUnsavedChangesChanged,
    showAddToPlanner = false,
    showPdfPrint = false,
    editableParticipants = true,
    hideWarnings = false,
    altPatient = false,
    // Context values now passed as props with defaults
    router = { replace: (path) => window.location.href = path },
    isPro = false,
    isMobile = window.innerWidth < 768,
    isCordova = false,
    showLoginForm,
    showUpgradeForm,
}) => {
    // Profile helper function (defined first since it's used in useMemo)
    const getProfileFromProps = useCallback((props, user) => {
        const { patient, altPatient } = props;

        // Determine profile source and type
        // If altPatient is true, use the alternate user for profile construction
        const profileUser = altPatient ? UserStore.getAlternateUser() : user;
        const profileSource = patient || profileUser || {};
        let type = patient ? 'patient' : (altPatient ? 'alternate-patient' : 'user');

        let {
            preferences = {diets: [], avoidances: [], exclude_foods: [], leftovers_enabled: true, max_leftover_days: 1},
            conditions = [{name: 'General Healthy Diet', uuid: '598069a0-b852-4eba-b498-15c46eef31af'}],
            family = [],
            hide_nutrition = false,
            ...rest
        } = profileSource;

        // Deep clone preferences to avoid mutations
        preferences = JSON.parse(JSON.stringify(preferences));

        // Ensure meal_types exists with proper defaults for alternate patients
        if (altPatient && (!preferences.meal_types || !Array.isArray(preferences.meal_types))) {
            preferences.meal_types = [
                { name: 'Breakfast', main_dish: 'Breakfast', side_dish: 'Breakfast Side Dish', participants: ['USER'], begin: 420, end: 540, pick_seq: 1 },
                { name: 'Morning Snack', main_dish: 'Snack', participants: ['USER'], begin: 600, end: 660, pick_seq: 2 },
                { name: 'Lunch', main_dish: 'Lunch', side_dish: 'Lunch Side Dish', participants: ['USER'], begin: 720, end: 840, pick_seq: 3 },
                { name: 'Dinner', main_dish: 'Dinner', side_dish: 'Side Dish', participants: ['USER'], begin: 1080, end: 1200, pick_seq: 4 }
            ];
        }

        // Set rd_override flag when viewing a patient's profile (dietitian context)
        if (patient) {
            preferences.rd_override = true;
        }

        return JSON.parse(JSON.stringify({
            type,
            preferences,
            conditions,
            family,
            hide_nutrition,
            ...rest
        }));
    }, []);

    // State
    const [visibleOffset, setVisibleOffset] = useState(0);
    const [showExplain, setShowExplain] = useState(true);
    const [dateSet, setDateSet] = useState(false);
    const [leftoverParams, setLeftoverParams] = useState(null);

    // Get user and profile
    const user = UserStore.getUser(); // Always use the logged-in user
    
    // Use the favorites/board management hook early to get stored profile data
    const tempFavorites = useFavorites({ uuid }, null, null);
    
    const profile = useMemo(() => {
        // Priority 1: A specific patient outranks everything. The same plan can also sit in
        // a favourites board carrying the profile it was built for, but when we are looking
        // at it *for* a patient it is that patient's household that matters — theirs is the
        // one the plan gets reconciled and scheduled against (EAT-3922).
        if (isPro && patient) {
            return getProfileFromProps({ patient, location, altPatient }, user);
        }

        // Priority 2: stored profile from favorites — genericized when no patient is in context.
        if (tempFavorites.boardContent?.profile) {
            return patient
                ? tempFavorites.boardContent.profile
                : genericizeProfileParticipants(tempFavorites.boardContent.profile);
        }

        // Priority 3: Use provided profile if available (from Favorites page context)
        if (providedProfile && Object.keys(providedProfile).length > 0) {
            return providedProfile;
        }

        // Priority 4: For PRO users with altPatient (Try It Out / Quick Start), use alternate user profile
        if (isPro && !patient && altPatient) {
            return getProfileFromProps({ patient, location, altPatient }, user);
        }

        // Priority 5: For PRO users browsing favorites without a specific patient context, use empty profile
        if (isPro && !patient) {
            return {
                preferences: {
                    diets: [],
                    avoidances: [],
                    exclude_foods: [],
                    leftovers_enabled: true,
                    max_leftover_days: 1,
                    meal_types: [
                        { name: 'Breakfast', main_dish: 'Breakfast', side_dish: 'Breakfast Side Dish', participants: ['USER'], begin: 420, end: 540, pick_seq: 1 },
                        { name: 'Morning Snack', main_dish: 'Snack', participants: ['USER'], begin: 600, end: 660, pick_seq: 2 },
                        { name: 'Lunch', main_dish: 'Lunch', side_dish: 'Lunch Side Dish', participants: ['USER'], begin: 720, end: 840, pick_seq: 3 },
                        { name: 'Dinner', main_dish: 'Dinner', side_dish: 'Side Dish', participants: ['USER'], begin: 1080, end: 1200, pick_seq: 4 }
                    ]
                },
                conditions: [{name: 'General Healthy Diet', uuid: '598069a0-b852-4eba-b498-15c46eef31af'}],
                family: []
            };
        }
        
        // Priority 6: Fallback to constructing from user/patient props (for consumer context)
        return getProfileFromProps({ patient, location, altPatient }, user);
    }, [tempFavorites.boardContent, providedProfile, patient, location, altPatient, user, getProfileFromProps]);

    // Use the comprehensive meal plan hook
    const mealPlan = useMealPlan({
        planId: uuid,
        profile: profile,
        user: user,
        router: router,
        envelope: profile?.nutrient_targets,
        autoSave: false,
        autoSaveDelay: 1500,
        showUpgradeForm: showUpgradeForm,
        showLoginForm: showLoginForm,
        isCordova: isCordova,
        onPlanUuidChanged: onPlanUuidChanged,
    });

    // Use the favorites/board management hook with the actual plan and save function
    const favorites = useFavorites(mealPlan.plan, mealPlan.savePlan, profile);

    // Log stored profile data if available  
    useEffect(() => {
        if (mealPlan.plan && favorites.boardContent) {
            // Plan editor opened with stored board content
        }
    }, [mealPlan.plan, favorites.boardContent, profile]);

    // Also expose key properties for easier access
    const { modalManager, mealManager } = mealPlan;

    /**
     * Handle sending plan to patient with BAA check
     */
    const startSendToPatient = useCallback(() => {
        // Check if user needs to sign BAA
        if (user && user.agreement_version !== '10.2017') {
            modalManager.showBAA(() => {
                // After BAA is accepted, proceed with recommend modal
                startRecommendWithAnalytics(mealPlan.plan, 'plan');
            });
            return;
        }

        // If no BAA needed, go directly to recommend modal
        startRecommendWithAnalytics(mealPlan.plan, 'plan');
    }, [user, modalManager, mealPlan.plan, startRecommendWithAnalytics]);

    // Effect to listen for user store changes
    useEffect(() => {
        const handleUserStoreChange = () => {
            // Force re-render when user changes
            // The profile will be recalculated automatically due to the dependency
        };

        UserStore.addChangeListener(handleUserStoreChange);
        return () => {
            UserStore.removeChangeListener(handleUserStoreChange);
        };
    }, []);

    // Compute permissions
    const permissions = useMemo(() => {
        if (!mealPlan.permissions) return {};

        return getPermissions({
            user,
            profile,
            plan: mealPlan.plan,
            canWrite: mealPlan.permissions.canWrite || false,
            canPublish: mealPlan.permissions.canPublish || false,
            canCreate: mealPlan.permissions.canCreate || false
        });
    }, [user, profile, mealPlan]);

    // Handle recipe/food save from edit modals
    const handleRecipeSaved = useCallback((updatedRecipe, updatedDetails) => {
        const context = modalManager.editRecipeContext;
        const meal = context?.meal;

        // If the recipe was saved as a new copy (different UUID),
        // update the meal item to reference the new recipe/details
        if (meal && updatedRecipe?.uuid && updatedRecipe.uuid !== context.recipe.uuid) {
            const mealKey = meal.uuid || meal.id;
            if (mealKey) {
                const updatedItems = (mealPlan.plan.items || []).map(item => {
                    if ((item.uuid || item.id) === mealKey) {
                        return {
                            ...item,
                            recipe_uuid: updatedRecipe.uuid,
                            details_uuid: updatedDetails?.uuid || item.details_uuid,
                        };
                    }
                    return item;
                });
                mealPlan.updatePlan({ items: updatedItems });
            }
        }

        mealPlan.refreshAfterEdit();
        modalManager.closeEditRecipe();
    }, [modalManager, mealPlan]);

    const handleFoodSaved = useCallback((savedFood) => {
        const meal = modalManager.editFoodContext?.meal;

        // If a new food was created (e.g. editing a live/system food creates a copy),
        // update the meal item to reference the new UUID so assets re-fetch picks it up.
        // Guard on a truthy mealKey so undefined === undefined cannot match every item.
        if (meal && savedFood && savedFood.uuid !== meal.food_uuid) {
            const mealKey = meal.uuid || meal.id;
            if (mealKey) {
                const updatedItems = (mealPlan.plan.items || []).map(item =>
                    (item.uuid || item.id) === mealKey
                        ? { ...item, food_uuid: savedFood.uuid }
                        : item
                );
                mealPlan.updatePlan({ items: updatedItems });
            }
        }

        mealPlan.refreshAfterEdit();
        modalManager.closeEditFood();
    }, [modalManager, mealPlan]);

    // Analytics wrapper functions for modal operations
    const showNutritionWithAnalytics = useCallback(() => {
        Analytics.viewMealPlanNutrition({
            'Meal Plan UUID': mealPlan.plan.uuid,
            'Meal Plan Name': mealPlan.plan.title
        });
        modalManager.showNutrition();
    }, [modalManager, mealPlan.plan]);

    const startRecommendWithAnalytics = useCallback((item, type) => {
        if (type === 'plan') {
            Analytics.startRecommendation();
        }
        modalManager.startRecommend(item, type);
    }, [modalManager]);

    // Notify parent component when unsaved changes status changes
    useEffect(() => {
        if (onUnsavedChangesChanged && mealPlan.saveState) {
            onUnsavedChangesChanged(mealPlan.saveState.isDirty);
        }
    }, [onUnsavedChangesChanged, mealPlan.saveState?.isDirty]);

    // Render loading states
    if (mealPlan.loading) {
        return (
            <div className="plan-editor">
                <div className="editor-loading">
                    <i className="icon-spinner2" /> Loading meal plan...
                </div>
            </div>
        );
    }

    if (mealPlan.error) {
        const isNotFound = mealPlan.error.toLowerCase().includes('not found');
        return (
            <div className="plan-editor">
                <div className="editor-loading">
                    <Alert
                        type="error"
                        title={isNotFound ? 'Meal Plan Not Found' : 'Error Loading Meal Plan'}
                        description={
                            isNotFound
                                ? 'The meal plan you are looking for could not be found. It may have been deleted or you may not have permission to view it.'
                                : `We encountered an error while loading your meal plan: ${mealPlan.error}`
                        }
                        dismissible={false}
                        onClickButton={() => router.replace('/')}
                        buttonText={<><i className="feather feather-arrow-left" /> Go Back</>}
                    />
                </div>
            </div>
        );
    }

    if (!mealPlan.plan && mealPlan.loading) {
        return (
            <div className="plan-editor">
                <div className="editor-loading">Initializing editor...</div>
            </div>
        );
    }

    return (
        <PermissionsProvider
            permissions={permissions}
            user={user}
            profile={profile}
            plan={mealPlan.plan}
            isPro={isPro}
        >
            <AssetsProvider assets={mealPlan.assets}>
                <MealPlanProvider
                    plan={mealPlan.plan}
                    user={user}
                    patient={patient}
                    profile={profile}
                    mealTypes={mealPlan.mealTypes}
                    maxOffset={mealPlan.maxOffset}
                    editableParticipants={editableParticipants}
                    synced={true}
                    analysis={mealPlan.nutrition?.analysis}
                    groceries={mealPlan.groceries}
                >
                    <MealOperationsProvider
                        mealManager={mealManager}
                    >
                        <ModalProvider
                            showMealDetails={modalManager.openMealDetailsModal}
                            startRepeatMeal={modalManager.startRepeatMeal}
                            showUpgradeForm={showUpgradeForm}
                            showGroceries={modalManager.showGroceries}
                            showNutrition={showNutritionWithAnalytics}
                            showProfile={modalManager.showProfile}
                            editMealBatches={modalManager.editMealBatches}
                            editRecipe={modalManager.editRecipe}
                            editFood={modalManager.editFood}
                            recommendRecipe={(recipe) => startRecommendWithAnalytics(recipe, 'recipe')}
                            recommendPlan={(plan) => startRecommendWithAnalytics(plan, 'plan')}
                            getModalData={modalManager.getModalData}
                        >
                            <UIStateProvider
                                addSwapContext={modalManager.addSwapContext}
                                hideWarnings={hideWarnings}
                                isMobile={isMobile}
                                router={router}
                            >
                                <div className="plan-editor">
                <Helmet>
                    <title>{mealPlan.plan?.name || 'Meal Plan'} - EatLove PRO</title>
                </Helmet>

                {/* Header */}
                <PrimaryActions
                    plan={mealPlan.plan}
                    permissions={permissions}
                    onUpdatePlan={mealPlan.updatePlan}
                    showPdfPrint={showPdfPrint}
                    showAddToPlanner={showAddToPlanner}
                    startSendToPatient={startSendToPatient}
                    onSchedulePlan={() => mealPlan.schedulePlan.startScheduling(mealPlan.saveState.isDirty)}
                    canSchedule={mealPlan.schedulePlan.canSchedule}
                    patient={patient}
                    user={user}
                    isPro={isPro}
                    isMobile={isMobile}
                    // Board/Favoriting props
                    onFavorite={favorites.handleFavoriteClick}
                    onBeforeUnfavorite={favorites.checkRecommendations}
                    boarded={favorites.boarded}
                    saving={mealPlan.saveState.isSaving}
                    // Save plan props
                    saveState={mealPlan.saveState}
                    savePlan={mealPlan.savePlan}
                    justSaved={mealPlan.justSaved}
                    // Toolbar actions (for mobile "More" menu)
                    clearPlan={mealPlan.clearPlan}
                    showGroceries={modalManager.showGroceries}
                    addDay={mealPlan.addDay}
                    showProfile={modalManager.showProfile}
                    maxOffset={mealPlan.maxOffset}
                    // Duplicate props
                    duplicatePlan={mealPlan.duplicatePlan}
                    isDuplicating={mealPlan.isDuplicating}
                />

                {/* Main Content */}
                <div className="plan-editor-content">

                    {/* Dietitian Banner */}
                    {!patient && (
                        <DietitianBanner
                            plan={mealPlan.plan}
                            patient={patient}
                            profile={profile}
                            maxOffset={mealPlan.maxOffset}
                        />
                    )}

                    {/* Meal Grid */}
                    <div className="mobile-grid-wrapper">
                        <DndProvider backend={HTML5Backend}>
                            <MobileGrid
                                plan={mealPlan.plan}
                                profile={profile}
                                mealTypes={mealPlan.mealTypes}
                                maxOffset={mealPlan.maxOffset}
                                visibleOffset={visibleOffset}
                                onOffsetChange={setVisibleOffset}
                                permissions={permissions}
                            />
                        </DndProvider>
                    </div>

                    {/* Secondary Actions */}
                    <SecondaryActions
                        user={user}
                        plan={mealPlan.plan}
                        maxOffset={mealPlan.maxOffset}
                        addDay={mealPlan.addDay}
                        permissions={permissions}
                        clearPlan={mealPlan.clearPlan}
                        duplicatePlan={mealPlan.duplicatePlan}
                        isDuplicating={mealPlan.isDuplicating}
                        isPro={isPro}
                    />

                    {/* Menu Stats */}
                    <MenuStats
                        plan={mealPlan.plan}
                        user={user}
                        profile={profile}
                        analysis={mealPlan.nutrition?.analysis}
                    />
                </div>

                {/* Modals */}
                {renderModals()}


                                </div>
                            </UIStateProvider>
                        </ModalProvider>
                    </MealOperationsProvider>
                </MealPlanProvider>
            </AssetsProvider>
        </PermissionsProvider>
    );

    // Render modals helper
    function renderModals() {
        const modals = modalManager.openModals;

        return (
            <>
                {/* Add/Swap Recipe Modal */}
                {modals.addSwapRecipe && (
                    <AddSwapWrapper
                        modalManager={modalManager}
                    />
                )}

                {/* Nutrition Modal */}
                {modals.showNutrition && (
                    <NutritionModal
                        closeModal={() => modalManager.closeModal('showNutrition')}
                    />
                )}

                {/* Pick Date Modal */}
                {modals.pickDate && (
                    <PickDateModal
                        dateStart={mealPlan.planDates.getMoment(mealPlan.planDates.startDate)}
                        formatDate={mealPlan.planDates.formatDate}
                        onPickDate={(date, offset) => {
                            modalManager.handleDateSelection(date, offset);
                        }}
                        closeModal={() => modalManager.closeModal('pickDate')}
                    />
                )}

                {/* Confirm Overwrite Modal */}
                {modalManager.isOverlapModalOpen && (
                    <ConfirmOverwriteModal
                        closeModal={(shouldCloseAll) => {
                            if (shouldCloseAll) {
                                modalManager.closeAllModals();
                            } else {
                                modalManager.closeOverlapModal();
                            }
                        }}
                    />
                )}

                {/* Meal Repeat Modal */}
                {modals.repeatMeal && (
                    <MealRepeatModal
                        meals={modalManager.mealsToRepeat}
                        closeModal={() => modalManager.closeModal('repeatMeal')}
                        addMeals={mealManager.addMeals}
                        applyOverlapResolution={mealManager.applyOverlapResolution}
                        modalManager={modalManager}
                    />
                )}

                {/* Meal Details Modal */}
                {modals.mealDetails && (
                    <MealDetails
                        offset={modalManager.selectedMealForDetails.offset}
                        mealType={modalManager.selectedMealForDetails.mealType}
                        closeModal={modalManager.closeMealDetailsModal}
                    />
                )}

                {/* Groceries Modal */}
                {modals.showGroceries && (
                    <GroceriesModal
                        closeModal={modalManager.closeGroceries}
                        profile={profile}
                    />
                )}

                {/* Profile Modal */}
                {modals.showProfile && (
                    <ProfileModal
                        closeModal={modalManager.closeProfile}
                        user={user}
                    />
                )}

                {/* Edit Meal Servings Modal */}
                {modals.editMealServings && (
                    <EditMealServingsWrapper
                        modalManager={modalManager}
                        addDay={mealPlan.addDay}
                        applyOverlapResolution={mealManager.applyOverlapResolution}
                    />
                )}

                {/* Recipe Editor Modal */}
                {modals.editRecipe && modalManager.editRecipeContext && (
                    <RecipeEditorModal
                        uuid={modalManager.editRecipeContext.recipe.uuid}
                        profile={profile}
                        isOpen={true}
                        closeModal={modalManager.closeEditRecipe}
                        onSave={handleRecipeSaved}
                    />
                )}

                {/* Food Editor Modal */}
                {modals.editFood && modalManager.editFoodContext && (
                    <FoodEditorModal
                        modalTitle="Edit Custom Food"
                        contentLabel="Edit Custom Food"
                        food={modalManager.editFoodContext.food}
                        profile={profile}
                        isOpen={true}
                        closeModal={modalManager.closeEditFood}
                        onSaveFood={handleFoodSaved}
                    />
                )}

                {/* Recommend Modal */}
                {modals.recommend && modalManager.recommendContext && (
                    <RecommendModal
                        patient={patient}
                        recipe={modalManager.recommendContext.type === 'recipe' ? modalManager.recommendContext.item : null}
                        plan={modalManager.recommendContext.type === 'plan' ? modalManager.recommendContext.item : null}
                        closeModal={modalManager.closeRecommend}
                        dirty={mealPlan.isDirty}
                        replaceMealPlan={mealPlan.updatePlan}
                    />
                )}

                {/* BAA Modal */}
                {modals.baa && modalManager.baaContext && (
                    <AgreementModal
                        closeModal={modalManager.closeBAA}
                        onAccept={() => {
                            modalManager.closeBAA();
                            if (modalManager.baaContext.onAccept) {
                                modalManager.baaContext.onAccept();
                            }
                        }}
                    />
                )}

                {/* Log Portions Modal */}
                {modals.logPortions && modalManager.logPortionsContext && (
                    <LogPortionsModalWrapper
                        contents={modalManager.logPortionsContext.contents}
                        profile={profile}
                        defaultPortions={modalManager.logPortionsContext.defaultPortions}
                        requirePrecise={modalManager.logPortionsContext.requirePrecise || false}
                        modalTitle={modalManager.logPortionsContext.modalTitle || "How much?"}
                        ctaText={modalManager.logPortionsContext.ctaText || "Add Food"}
                        onSelectPortions={modalManager.logPortionsContext.onSelectPortions}
                        closeModal={modalManager.closeLogPortions}
                    />
                )}

                {/* Pick Date Modal */}
                {modals.pickDate && modalManager.pickDateContext && (
                    <PickDateModal
                        dateStart={mealPlan.schedulePlan?.selectedDate}
                        onPickDate={modalManager.pickDateContext.onPickDate}
                        closeModal={() => {
                            // Cancel the scheduling flow to reset all states
                            if (mealPlan.schedulePlan?.cancelScheduling) {
                                mealPlan.schedulePlan.cancelScheduling();
                            } else {
                                modalManager.closePickDate();
                            }
                        }}
                    />
                )}

                {/* Leftover Offsets Modal */}
                {modals.leftoverOffsets && modalManager.leftoverOffsetsContext && (
                    <LeftoverOffsetsModal
                        user={profile}
                        plan={mealPlan.plan}
                        recipes={mealPlan.assets.recipes}
                        foods={mealPlan.assets.foods}
                        leftoverParams={modalManager.leftoverOffsetsContext}
                        closeModal={modalManager.closeLeftoverOffsetsModal}
                        rescheduleMealParams={modalManager.leftoverOffsetsContext.mealsToReschedule ? {
                            meals: modalManager.leftoverOffsetsContext.mealsToReschedule,
                            offset: modalManager.leftoverOffsetsContext.targetOffset,
                            mealType: modalManager.leftoverOffsetsContext.targetMealType
                        } : null }
                        rescheduleMeals={mealManager.rescheduleMeals}
                        onSelectLeftoverOffsets={modalManager.leftoverOffsetsContext.onSelectLeftoverOffsets || ((leftovers, scalingMap, extraServings = 0) => {
                            // Default handler for leftover selection in hook-based system
                            // This mimics the consumer's flow: LeftoverDatesModal → MealActionsWrapper.onSelectLeftoverDates → onChangeParticipants
                            const { meal } = modalManager.leftoverOffsetsContext;

                            // Apply extraServings to the original meal if specified
                            if (extraServings > 0) {
                                const updatedMeal = {
                                    ...meal,
                                    extra_servings: (meal.extra_servings || 0) + extraServings
                                };
                                mealManager.applyOverlapResolution([updatedMeal], [], []);
                            }

                            // Use centralized leftover creation utility (like consumer's updateMealLeftovers)
                            const leftoverMeals = mealManager.createLeftoverMeals(meal, leftovers, scalingMap);

                            // Apply the leftover meals using meal management (like consumer's onModifyMeals)
                            mealManager.applyOverlapResolution(leftoverMeals, [], []);
                            modalManager.closeLeftoverOffsetsModal();
                        })}
                        returnMode={modalManager.leftoverOffsetsContext?.returnMode || false}
                        onReturnLeftoverData={(leftovers, scalingMap, extraServings = 0) => {
                            // Handle return mode for manual approval

                            // Pass the leftover data to EditMealServingsWrapper via modalManager
                            modalManager.setEditMealServingsContext({
                                ...modalManager.editMealServingsContext,
                                pendingLeftovers: { leftovers, scalingMap, extraServings }
                            });

                            modalManager.closeLeftoverOffsetsModal();
                        }}
                        onShowConflictModal={(leftovers, scalingMap, extraServings = 0) => {
                            // Handle conflicts - differentiate between return mode (EditMealServings) and normal mode (Swap)
                            const isReturnMode = modalManager.leftoverOffsetsContext?.returnMode || false;

                            // Convert leftover data to overlap structure expected by ConfirmOverwriteModal
                            const overlaps = leftovers.filter(leftover => leftover.conflictingMeals.length > 0)
                                .map((leftover, index) => {
                                    // Create leftover meal object for the leftover being created
                                    const leftoverMeal = {
                                        id: generateUuid(),
                                        recipe_uuid: modalManager.leftoverOffsetsContext?.meal?.recipe_uuid,
                                        food_uuid: modalManager.leftoverOffsetsContext?.meal?.food_uuid,
                                        meal: leftover.mealType,
                                        offset: leftover.leftoverOffset,
                                        meal_type: 'leftover',
                                        participants: leftover.participants.map(p => p.uuid || ''), // Convert objects to UUID strings
                                        parent: modalManager.leftoverOffsetsContext?.meal?.id
                                    };

                                    return {
                                        mealType: leftover.mealType,
                                        offset: leftover.leftoverOffset,
                                        destMeals: leftover.conflictingMeals,
                                        sourceMeals: [leftoverMeal],
                                        action: 'replace' // Default action
                                    };
                                });

                            if (isReturnMode) {
                                // Return mode: send conflict data back to EditMealServingsWrapper
                                modalManager.openOverlapModal({
                                    overlaps: overlaps,
                                    returnMode: true,
                                    isRecipeSearch: true, // Hide swap option for leftover creation
                                    pendingLeftovers: { leftovers, scalingMap, extraServings },
                                    onReturnConflictData: (conflictResolutions) => {
                                        modalManager.setEditMealServingsContext({
                                            ...modalManager.editMealServingsContext,
                                            pendingLeftovers: {
                                                leftovers: leftovers,
                                                scalingMap: scalingMap,
                                                extraServings: extraServings,
                                                conflictResolutions: conflictResolutions
                                            }
                                        });
                                        modalManager.closeOverlapModal();
                                    }
                                });
                            } else {
                                // Normal swap mode: pass main meal to conflict modal like consumer side
                                const mainMeal = modalManager.leftoverOffsetsContext?.meal;
                                const mealsToDelete = modalManager.leftoverOffsetsContext?.mealsToDelete;
                                const isRecipeSelection = modalManager.leftoverOffsetsContext?.isRecipeSelection || false;

                                modalManager.openOverlapModal({
                                    overlaps: overlaps,
                                    returnMode: false,
                                    isRecipeSearch: isRecipeSelection, // Hide swap option when creating leftovers from recipe
                                    additionalMealToAdd: mainMeal, // Pass main meal to be added with leftovers
                                    additionalMealsToDelete: mealsToDelete // Pass meals to delete (for swap mode)
                                });
                            }
                            
                            modalManager.closeLeftoverOffsetsModal();
                        }}
                    />
                )}

                {/* Working Veil for scheduling */}
                <WorkingVeil
                    isVisible={mealPlan.schedulePlan?.isWorking || false}
                    message="Crunching the numbers..."
                />
            </>
        );
    }
};

Editor.propTypes = {
    uuid: PropTypes.string,
    patient: PropTypes.object,
    profile: PropTypes.object,
    location: PropTypes.object,
    closeModal: PropTypes.func,
    onSavePlan: PropTypes.func,
    onPlanUuidChanged: PropTypes.func,
    onUnsavedChangesChanged: PropTypes.func,
    showAddToPlanner: PropTypes.bool,
    showPdfPrint: PropTypes.bool,
    editableParticipants: PropTypes.bool,
    hideWarnings: PropTypes.bool,
    altPatient: PropTypes.bool,
    // Context props
    router: PropTypes.shape({
        push: PropTypes.func.isRequired,
        replace: PropTypes.func.isRequired
    }).isRequired,
    isPro: PropTypes.bool,
    isMobile: PropTypes.bool,
    isCordova: PropTypes.bool,
    showLoginForm: PropTypes.func.isRequired,
    showUpgradeForm: PropTypes.func.isRequired,
};

export default Editor;
